import React, { ReactNode, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import Sidebar from './Sidebar';
import Header from './Header';

const LayoutContainer = styled.div`
  display: flex;
  height: 100dvh;
  min-width: 1024px;
  background-color: ${({ theme }) => theme.color.primary_200};
  overflow: hidden;
`;

const SidebarWrapper = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
`;

const ResizeHandle = styled.div`
  width: 3px;
  cursor: col-resize;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
  transition: background-color 0.2s;

  &:hover, &:active {
    background-color: ${({ theme }) => theme.color.point_y_100};
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const PageContent = styled.div`
  padding: 20px 24px 28px;
  min-height: 0;
  overflow-y: auto;
  flex: 1;
`;

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(224);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing && !isCollapsed) {
        const newWidth = mouseMoveEvent.clientX;
        if (newWidth > 160 && newWidth < 400) {
          setSidebarWidth(newWidth);
        }
      }
    },
    [isResizing, isCollapsed]
  );

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  return (
    <LayoutContainer>
      <SidebarWrapper>
        <Sidebar
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          width={isCollapsed ? 64 : sidebarWidth}
        />
        {!isCollapsed && <ResizeHandle role="separator" aria-label="메뉴 너비 조절" aria-orientation="vertical" aria-valuemin={160} aria-valuemax={400} aria-valuenow={sidebarWidth} tabIndex={0} onMouseDown={startResizing} onKeyDown={event => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); setSidebarWidth(width => Math.max(160, Math.min(400, width + (event.key === 'ArrowRight' ? 16 : -16)))); }
        }} />}
      </SidebarWrapper>
      <MainContent>
        <Header />
        <PageContent>
          {children}
        </PageContent>
      </MainContent>
    </LayoutContainer>
  );
};

export default MainLayout;
